import { useState, useMemo } from "react";
import { CheckCircle2, XCircle, AlertCircle, ArrowRight, ArrowLeft, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

// 6-step interactive 407 Training Visa eligibility check.
// Original StudyNet content based on publicly verifiable Subclass 407 criteria
// from the Department of Home Affairs (immi.homeaffairs.gov.au).

const STEPS = [
  {
    key: "age",
    title: "Are you at least 18 years old?",
    help: "All Subclass 407 applicants must be 18 or over at the time of application.",
    options: [
      { label: "Yes — I'm 18 or over", value: "yes", good: true },
      { label: "No — I'm under 18", value: "no", good: false, reason: "Subclass 407 is only open to applicants 18 and over." },
    ],
  },
  {
    key: "sponsor",
    title: "Do you have an Australian Temporary Activities Sponsor?",
    help: "Eligible sponsors include businesses lawfully operating in Australia, Commonwealth/state agencies and certain overseas entities.",
    options: [
      { label: "Yes — I have a confirmed sponsor", value: "yes", good: true },
      { label: "Not yet — exploring options", value: "later", good: true, soft: true },
      { label: "No — I don't have one", value: "no", good: false, reason: "A 407 application requires an approved Temporary Activities Sponsor and nomination." },
    ],
  },
  {
    key: "stream",
    title: "Which training stream best fits your situation?",
    help: "Choose the stream most aligned with the purpose of your training.",
    options: [
      { label: "Required for registration / professional development in my home country", value: "registration", good: true },
      { label: "Enhancing skills in an eligible occupation", value: "enhancement", good: true },
      { label: "Capacity-building program endorsed by a government agency", value: "capacity", good: true },
      { label: "None of the above", value: "none", good: false, reason: "The 407 visa requires one of the three structured training streams." },
    ],
  },
  {
    key: "experience",
    title: "Do you have at least 12 months of recent work or study in the nominated occupation?",
    help: "Typically 12+ months of relevant experience in the last 24 months is expected.",
    options: [
      { label: "Yes — 12+ months relevant experience or study", value: "yes", good: true },
      { label: "Partly — close but not yet", value: "partly", good: true, soft: true },
      { label: "No relevant background", value: "no", good: false, reason: "Inadequate occupational background is one of the most common 407 refusal grounds." },
    ],
  },
  {
    key: "english",
    title: "Do you have functional English?",
    help: "Demonstrated through IELTS/PTE, secondary/tertiary study in English, or equivalent evidence.",
    options: [
      { label: "Yes — I can evidence functional English", value: "yes", good: true },
      { label: "Not sure — need to check", value: "unsure", good: true, soft: true },
      { label: "No", value: "no", good: false, reason: "Functional English is a core 407 requirement." },
    ],
  },
  {
    key: "intent",
    title: "Do you intend to return home or transition lawfully after training?",
    help: "Genuine intention is a key criterion. Strategy for a possible PR pathway is fine — but must be lawful.",
    options: [
      { label: "Yes", value: "yes", good: true },
      { label: "Need advice on long-term pathway", value: "advice", good: true, soft: true },
      { label: "No — I want to stay regardless", value: "no", good: false, reason: "Lack of genuine intention is a frequent refusal trigger." },
    ],
  },
];

export default function TrainingVisaWizard() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [finished, setFinished] = useState(false);

  const result = useMemo(() => {
    if (!finished) return null;
    const chosen = STEPS.map((s) => {
      const v = answers[s.key];
      return s.options.find((o) => o.value === v);
    });
    const blocker = chosen.find((c) => c && c.good === false);
    if (blocker) {
      return { kind: "blocked", reason: blocker.reason };
    }
    const soft = chosen.filter((c) => c && c.soft).length;
    if (soft >= 2) {
      return { kind: "borderline" };
    }
    return { kind: "good" };
  }, [finished, answers]);

  const reset = () => { setStep(0); setAnswers({}); setFinished(false); };
  const current = STEPS[step];

  const pickOption = (opt) => {
    const next = { ...answers, [current.key]: opt.value };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setFinished(true);
  };

  const progress = Math.round(((step + (finished ? 1 : 0)) / STEPS.length) * 100);

  return (
    <section data-testid="training-407-wizard" className="py-16 md:py-20 bg-[#F8F4FB] border-t border-slate-200">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">
            Interactive eligibility check
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight">
            407 Training Visa — Check Your Eligibility
          </h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Answer 6 quick questions to see whether the Subclass 407 Training Visa is a realistic pathway for you. Indicative only — not migration advice.
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2 text-[11px] tracking-wider uppercase text-slate-500 font-bold">
            <span>Step {finished ? STEPS.length : step + 1} of {STEPS.length}</span>
            <span data-testid="wizard-progress">{progress}%</span>
          </div>
          <div className="h-1.5 w-full bg-white border border-slate-200">
            <div
              className="h-full bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {!finished ? (
          <div data-testid={`wizard-step-${step}`} className="bg-white border border-slate-200 p-6 md:p-8 shadow-sm">
            <h3 className="font-display text-xl md:text-2xl text-[#29324A] leading-snug mb-1">{current.title}</h3>
            <p className="text-[12px] text-slate-500 leading-relaxed mb-5">{current.help}</p>

            <div className="space-y-2.5">
              {current.options.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  data-testid={`wizard-opt-${current.key}-${opt.value}`}
                  onClick={() => pickOption(opt)}
                  className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 border border-slate-200 hover:border-[#C43A89] hover:bg-[#F8F4FB] transition-colors"
                >
                  <span className="text-sm text-[#29324A]">{opt.label}</span>
                  <ArrowRight className="h-4 w-4 text-[#29324A] shrink-0" />
                </button>
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                disabled={step === 0}
                onClick={() => setStep(Math.max(0, step - 1))}
                data-testid="wizard-back"
                className="text-[#29324A] hover:bg-[#F8F4FB]"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <button
                type="button"
                onClick={reset}
                data-testid="wizard-restart"
                className="text-[11px] tracking-wider uppercase text-slate-400 hover:text-[#C43A89] flex items-center gap-1 transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Restart
              </button>
            </div>
          </div>
        ) : (
          <div data-testid="wizard-result" className="bg-white border border-slate-200 p-6 md:p-9 shadow-sm">
            {result.kind === "good" && (
              <>
                <div className="h-16 w-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-5">
                  <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-[#29324A] text-center leading-snug mb-2">
                  Looking good — Subclass 407 may be a fit.
                </h3>
                <p className="text-sm text-slate-600 text-center max-w-md mx-auto leading-relaxed mb-7">
                  Based on your answers, the Training Visa appears realistic for you. The next step is a paid consultation so our Registered Migration Agents can review the training plan and your supporting documents in detail.
                </p>
              </>
            )}
            {result.kind === "borderline" && (
              <>
                <div className="h-16 w-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-5">
                  <AlertCircle className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-[#29324A] text-center leading-snug mb-2">
                  Borderline — a consultation will clarify the path.
                </h3>
                <p className="text-sm text-slate-600 text-center max-w-md mx-auto leading-relaxed mb-7">
                  You have several "not sure" or "partly" answers. A 30-minute consultation will identify whether your circumstances can be strengthened before lodgement.
                </p>
              </>
            )}
            {result.kind === "blocked" && (
              <>
                <div className="h-16 w-16 mx-auto rounded-full bg-rose-100 flex items-center justify-center mb-5">
                  <XCircle className="h-8 w-8 text-rose-600" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-[#29324A] text-center leading-snug mb-2">
                  Subclass 407 may not be the right fit today.
                </h3>
                <p className="text-sm text-slate-600 text-center max-w-md mx-auto leading-relaxed mb-3">
                  {result.reason}
                </p>
                <p className="text-sm text-slate-600 text-center max-w-md mx-auto leading-relaxed mb-7">
                  Other Australian work, study or visitor visa pathways may suit your situation. Speak with our team for a personalised review.
                </p>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Link
                to="/book-consultation"
                data-testid="wizard-book-cta"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase py-3.5 transition-colors btn-shimmer"
              >
                Book consultation <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <button
                type="button"
                onClick={reset}
                data-testid="wizard-restart-result"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-[#29324A] text-[#29324A] text-xs font-bold tracking-wider uppercase py-3.5 transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Start over
              </button>
            </div>

            <div className="mt-6 text-[11px] text-slate-500 text-center leading-relaxed">
              This eligibility check is general guidance based on publicly available Department of Home Affairs criteria. It is not migration advice and does not guarantee a visa outcome.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
